import React, { useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Mailer from "./components/Mailer";
import ConfirmationPage from "./components/ConfirmationPage";
import OutreachPage from "./pages/outreach/OutreachPage";
import CampaignDetailsPage from "./pages/outreach/CampaignDetailsPage";
import PendingOutreach from "./pages/PendingOutreach";
import UnsubscribeConfirmation from "./pages/UnsubscribeConfirmation";
import Login from "./components/auth/Login";
import Register from "./components/auth/Register";
import ForgotPassword from "./components/auth/ForgotPassword";
import Profile from "./components/auth/Profile";
import AdminSetup from "./components/auth/AdminSetup";
import AdminPanel from "./components/auth/AdminPanel";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import AuthLogsMonitoring from "./components/auth/AuthLogsMonitoring";
import AuthDashboard from "./components/auth/AuthDashboard";
import { PerformanceDashboard } from "./components/common/PerformanceDashboard";
import AnalyticsPage from "./pages/outreach/AnalyticsPage";
import { S3DragDropZone } from "./components/s3/S3DragDropZone";

// Initialize AWS Amplify configuration
import "./utils/amplify";
// Global styles
import "./styles/globals.css";
// Initialize the Firebase-Amplify bridge for S3 access
import { initializeAmplifyFirebaseBridge } from "./utils/amplifyFirebaseBridge";
// Auth network monitoring
import AuthNetworkMonitor from "./utils/authNetworkMonitor";

// Layout components
import Layout from "./components/layout/Layout";
import Dashboard from "./pages/dashboard/Dashboard";

import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { S3UploadProvider } from "./contexts/S3UploadContext";
import { S3UploadManagerWrapper } from "./components/s3/S3UploadManagerWrapper";
import { FeatureFlagProvider } from "./contexts/FeatureFlagContext";
import { WhatsNewProvider } from "./contexts/WhatsNewProvider";
import theme from "./utils/theme";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createLocalStoragePersister } from './utils/reactQueryPersister';
import { initializeCacheInvalidation } from './utils/cacheInvalidation';
import { initializeFallbackOptimization } from './utils/fallbackOptimizationManager';
import ErrorBoundary from './components/common/ErrorBoundary';

// Create persister for localStorage integration
const persister = createLocalStoragePersister();

// Configure React Query client with optimized settings for tracking data
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache tracking data for 5 minutes
      staleTime: 5 * 60 * 1000, // 5 minutes
      // Keep data in cache for 10 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      // Retry failed requests 2 times
      retry: 2,
      // Refetch on window focus for fresh data
      refetchOnWindowFocus: true,
      // Refetch on reconnect
      refetchOnReconnect: true,
    },
    mutations: {
      // Retry failed mutations once
      retry: 1,
    },
  },
});

// Initialize cache invalidation manager
const cacheInvalidationManager = initializeCacheInvalidation(queryClient);

// Initialize fallback optimization system
const fallbackOptimizationManager = initializeFallbackOptimization({
  criticalCampaigns: [], // Will be populated dynamically
  enablePerformanceMonitoring: true,
  cacheWarming: {
    prefetchOnStartup: true,
    backgroundRefreshInterval: 5 * 60 * 1000, // 5 minutes
    maxConcurrentWarmups: 3,
  },
  cachePruning: {
    maxCacheSize: 500,
    maxCacheAge: 24 * 60 * 60 * 1000, // 24 hours
    pruningInterval: 30 * 60 * 1000, // 30 minutes
    keepTopN: 100,
    criticalQueryKeys: ['tracking', 'campaigns', 'campaignStats'],
  },
});

// Secure DevTools component that only renders for authenticated users on Outreach pages
const SecureDevTools: React.FC = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  
  // Only show DevTools in development AND for authenticated users AND on Outreach pages
  const isOnOutreachPage = location.pathname.startsWith('/outreach');
  const shouldShowDevTools = process.env.NODE_ENV === 'development' && 
                            currentUser && 
                            isOnOutreachPage;
  
  if (!shouldShowDevTools) {
    return null;
  }
  
  return <ReactQueryDevtools initialIsOpen={false} />;
};

const App: React.FC = () => {
  // Initialize Amplify-Firebase bridge on app startup
  useEffect(() => {
    initializeAmplifyFirebaseBridge();
    
    // Start auth network monitoring
    const authNetworkMonitor = AuthNetworkMonitor.getInstance();
    authNetworkMonitor.startMonitoring();
    
    // Initialize fallback optimization system
    fallbackOptimizationManager.initialize().catch(error => {
      console.error('Failed to initialize fallback optimization:', error);
    });
    
    // Cleanup on unmount
    return () => {
      authNetworkMonitor.stopMonitoring();
      fallbackOptimizationManager.cleanup();
    };
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <FeatureFlagProvider>
        <AuthProvider>
          <WhatsNewProvider>
            <S3UploadProvider maxConcurrent={5}>
              <PersistQueryClientProvider 
                client={queryClient} 
                persistOptions={{
                  persister,
                  maxAge: 24 * 60 * 60 * 1000, // 24 hours
                  buster: '1.0', // Change this to invalidate all cached data
                  dehydrateOptions: {
                    // Only persist specific query types to save space
                    shouldDehydrateQuery: (query) => {
                      const queryKey = query.queryKey[0];
                      // Only persist tracking and campaign stats data
                      return queryKey === 'tracking' || queryKey === 'campaignStats';
                    },
                  },
                }}
              >
                <S3UploadManagerWrapper>
                  <ErrorBoundary>
                    <Routes>
                {/* Public routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/unsubscribe" element={<UnsubscribeConfirmation />} />

                {/* Protected routes with Layout */}
                <Route element={<ProtectedRoute />}>
                  <Route element={<Layout />}>
                    {/* Dashboard as the new landing page */}
                    <Route path="/dashboard" element={<Dashboard />} />

                    {/* Renamed from /payouts to /mailer */}
                    <Route path="/mailer/*" element={<Mailer />} />
                    <Route path="/confirmation" element={<ConfirmationPage />} />

                    {/* Other protected routes */}
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/admin-setup" element={<AdminSetup />} />
                    <Route path="/admin" element={<AdminPanel />} />
                    
                    {/* Outreach routes - URL-based navigation */}
                    <Route path="/outreach/analytics" element={<AnalyticsPage />} />
                    <Route path="/outreach/pending" element={<PendingOutreach />} />
                    <Route path="/outreach/:view" element={<OutreachPage />} />
                    <Route path="/outreach" element={<Navigate to="/outreach/campaigns" replace />} />
                    <Route
                      path="/outreach/campaign/:category/:id"
                      element={<CampaignDetailsPage />}
                    />
                    
                    {/* Auth monitoring routes */}
                    <Route path="/auth-logs" element={<AuthLogsMonitoring />} />
                    <Route path="/auth-dashboard" element={<AuthDashboard />} />
                    <Route path="/performance" element={<PerformanceDashboard />} />
                  </Route>
                </Route>

                {/* Redirect root to dashboard */}
                <Route path="/" element={<Navigate to="/dashboard" replace />} />

                {/* Redirect old payouts path to new mailer path for backward compatibility */}
                <Route path="/payouts" element={<Navigate to="/mailer" replace />} />

                {/* Fallback route */}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
                </ErrorBoundary>
                {/* Secure React Query DevTools - only for authenticated users on Outreach pages */}
                <SecureDevTools />
                {/* S3 Drag & Drop Zone - Portal-based, globally available for file uploads */}
                <S3DragDropZone />
              </S3UploadManagerWrapper>
            </PersistQueryClientProvider>
          </S3UploadProvider>
          </WhatsNewProvider>
        </AuthProvider>
      </FeatureFlagProvider>
    </ThemeProvider>
  );
};

export default App;
